import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Archive,
	ArrowLeft,
	ArrowUpDown,
	Ban,
	Building2,
	Calendar,
	ClipboardCheck,
	Clock,
	Download,
	Edit,
	Eye,
	FileText,
	Globe,
	Link2,
	Loader2,
	Lock,
	Mail,
	Play,
	Plus,
	Scale,
	Send,
	Target,
	Trash2,
	Trophy,
	Upload,
	User,
	Users,
} from "lucide-react";
import { useState, useRef } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import {
	getBidStatusColor,
	getStatusColor,
	getStatusLabel,
} from "@/lib/utils";
import { SolicitationQA } from "@/components/solicitation/SolicitationQA";
import { SolicitationSheets } from "@/components/solicitation/SolicitationSheets";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { PdfFormEditorV2 } from "@/components/pdf-editor";
import type { WidgetData } from "@/components/pdf-editor/types";

// Sheet definition for tabular form data export
interface SheetDefinition {
	formId: string;
	name: string;
	columnNames: string[];
	rowNames: string[];
	cellIds: string[][];
}

// Pending award form to be created when the award is submitted
interface PendingAwardForm {
	id: string; // Temporary client-side ID
	name: string;
	description: string;
	pdfStoragePath: string | null;
	widgets: WidgetData[];
	isRequired: boolean;
}

export const Route = createFileRoute("/solicitations/$id/")({
	component: SolicitationDetailPage,
});

// Form download/view button component
function FormDownloadButton({
	pdfPath,
	formTemplateId,
	pdfStoragePath,
	title,
}: {
	pdfPath?: string | null;
	formTemplateId?: string | null;
	pdfStoragePath?: string | null;
	title?: string;
}) {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const [isDownloading, setIsDownloading] = useState(false);
	const [isViewing, setIsViewing] = useState(false);

	const canDownload = pdfPath || pdfStoragePath || formTemplateId;

	const handleView = async () => {
		setIsViewing(true);
		try {
			// Try pdf_path first (legacy Supabase storage)
			if (pdfPath) {
				const result = await queryClient.fetchQuery(
					trpc.forms.templates.getDownloadUrl.queryOptions({ pdfPath })
				);
				if (result.signedUrl) {
					window.open(result.signedUrl, "_blank");
					return;
				}
			}

			// Use pdfStoragePath via pdfTemplates router
			if (formTemplateId) {
				const result = await queryClient.fetchQuery(
					trpc.pdfTemplates.downloadPdf.queryOptions({ templateId: formTemplateId })
				);
				if (result.pdfBase64) {
					const byteCharacters = atob(result.pdfBase64);
					const byteNumbers = new Array(byteCharacters.length);
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i);
					}
					const byteArray = new Uint8Array(byteNumbers);
					const blob = new Blob([byteArray], { type: "application/pdf" });
					const url = URL.createObjectURL(blob);
					window.open(url, "_blank");
					return;
				}
			}

			toast.error("Form template not available");
		} catch (error) {
			console.error("View error:", error);
			toast.error("Failed to view form");
		} finally {
			setIsViewing(false);
		}
	};

	const handleDownload = async () => {
		setIsDownloading(true);
		try {
			// Try pdf_path first (legacy Supabase storage)
			if (pdfPath) {
				const result = await queryClient.fetchQuery(
					trpc.forms.templates.getDownloadUrl.queryOptions({ pdfPath })
				);
				if (result.signedUrl) {
					// Fetch and download the file
					const response = await fetch(result.signedUrl);
					const blob = await response.blob();
					const url = URL.createObjectURL(blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = title ? `${title}.pdf` : "form.pdf";
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					URL.revokeObjectURL(url);
					return;
				}
			}

			// Use pdfStoragePath via pdfTemplates router
			if (formTemplateId) {
				const result = await queryClient.fetchQuery(
					trpc.pdfTemplates.downloadPdf.queryOptions({ templateId: formTemplateId })
				);
				if (result.pdfBase64) {
					// Create a blob and download
					const byteCharacters = atob(result.pdfBase64);
					const byteNumbers = new Array(byteCharacters.length);
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i);
					}
					const byteArray = new Uint8Array(byteNumbers);
					const blob = new Blob([byteArray], { type: "application/pdf" });
					const url = URL.createObjectURL(blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = title ? `${title}.pdf` : "form.pdf";
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					URL.revokeObjectURL(url);
					return;
				}
			}

			toast.error("Form template not available");
		} catch (error) {
			console.error("Download error:", error);
			toast.error("Failed to download form");
		} finally {
			setIsDownloading(false);
		}
	};

	return (
		<div className="flex items-center gap-1">
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						variant="ghost"
						size="sm"
						onClick={handleView}
						disabled={isViewing || !canDownload}
					>
						{isViewing ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Eye className="h-4 w-4" />
						)}
					</Button>
				</TooltipTrigger>
				<TooltipContent>View form{title ? `: ${title}` : ""}</TooltipContent>
			</Tooltip>
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						variant="ghost"
						size="sm"
						onClick={handleDownload}
						disabled={isDownloading || !canDownload}
					>
						{isDownloading ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Download className="h-4 w-4" />
						)}
					</Button>
				</TooltipTrigger>
				<TooltipContent>Download form{title ? `: ${title}` : ""}</TooltipContent>
			</Tooltip>
		</div>
	);
}

// Attachment row component with download functionality
function AttachmentRow({ file, formatFileSize }: {
	file: { id: string; title: string; file_path: string; file_size: number | null; created_at: string; description: string | null };
	formatFileSize: (bytes: number) => string;
}) {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const [isDownloading, setIsDownloading] = useState(false);
	const [isViewing, setIsViewing] = useState(false);

	const isPdf = file.file_path?.toLowerCase().endsWith(".pdf");

	const handleView = async () => {
		if (!file.file_path) {
			toast.error("File path not available");
			return;
		}

		setIsViewing(true);
		try {
			const result = await queryClient.fetchQuery(
				trpc.attachments.getDownloadUrl.queryOptions({ filePath: file.file_path })
			);
			if (result.signedUrl) {
				window.open(result.signedUrl, "_blank");
			}
		} catch (error) {
			toast.error("Failed to get file URL");
		} finally {
			setIsViewing(false);
		}
	};

	const handleDownload = async () => {
		if (!file.file_path) {
			toast.error("File path not available");
			return;
		}

		setIsDownloading(true);
		try {
			const result = await queryClient.fetchQuery(
				trpc.attachments.getDownloadUrl.queryOptions({ filePath: file.file_path })
			);
			if (result.signedUrl) {
				// Fetch the file and trigger download
				const response = await fetch(result.signedUrl);
				const blob = await response.blob();
				const url = URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = file.title || "document";
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				URL.revokeObjectURL(url);
			}
		} catch (error) {
			toast.error("Failed to download file");
		} finally {
			setIsDownloading(false);
		}
	};

	return (
		<div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
			<div className="flex items-center gap-3">
				<FileText className="h-5 w-5 text-muted-foreground" />
				<div>
					<p className="font-medium">{file.title}</p>
					<p className="text-sm text-muted-foreground">
						{file.file_size ? formatFileSize(file.file_size) : "Unknown size"}
						{" - "}
						Uploaded {new Date(file.created_at).toLocaleDateString()}
					</p>
					{file.description && (
						<p className="text-xs text-muted-foreground mt-1">
							{file.description}
						</p>
					)}
				</div>
			</div>
			<div className="flex items-center gap-1">
				{isPdf && (
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="sm"
								onClick={handleView}
								disabled={isViewing}
							>
								{isViewing ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Eye className="h-4 w-4" />
								)}
							</Button>
						</TooltipTrigger>
						<TooltipContent>View PDF</TooltipContent>
					</Tooltip>
				)}
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="ghost"
							size="sm"
							onClick={handleDownload}
							disabled={isDownloading}
						>
							{isDownloading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Download className="h-4 w-4" />
							)}
						</Button>
					</TooltipTrigger>
					<TooltipContent>Download: {file.title}</TooltipContent>
				</Tooltip>
			</div>
		</div>
	);
}

function SolicitationDetailPage() {
	const { id } = Route.useParams();
	const { hasPermission, user } = useAuth();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const [activeTab, setActiveTab] = useState("overview");
	const [isTransitioning, setIsTransitioning] = useState(false);
	const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Fetch solicitation data
	const { data: solicitation, isLoading: solicitationLoading } = useQuery(
		trpc.solicitations.get.queryOptions({ id }),
	);

	// Fetch buyer organization to get the slug for public link
	const { data: buyerOrg } = useQuery({
		...trpc.organizations.getBuyer.queryOptions({
			id: solicitation?.buyer_org_id ?? "",
		}),
		enabled: !!solicitation?.buyer_org_id,
	});

	// Mutations for state transitions
	const publishMutation = useMutation(trpc.solicitations.publish.mutationOptions());
	const transitionMutation = useMutation(trpc.solicitations.transition.mutationOptions());
	const closeBiddingMutation = useMutation(trpc.solicitations.closeBidding.mutationOptions());

	// Mutations for attachment upload
	const createSignedUploadUrlMutation = useMutation(
		trpc.attachments.createSignedUploadUrl.mutationOptions(),
	);
	const uploadAttachmentMutation = useMutation(
		trpc.attachments.upload.mutationOptions(),
	);

	// Award mutation
	const createAwardMutation = useMutation(
		trpc.awards.bids.create.mutationOptions(),
	);

	// Helper to get effective status (scheduled vs open vs awaiting_decision)
	// "scheduled" = open with publish_date in the future
	// "awaiting_decision" = open with due_date in the past (bidding closed)
	const getEffectiveStatus = (
		status: string,
		publishDate: string | null,
		dueDate: string | null,
	): string => {
		if (status === "open") {
			// Check if scheduled (publish_date in future)
			if (publishDate && new Date(publishDate) > new Date()) {
				return "scheduled";
			}
			// Check if awaiting decision (due_date in the past)
			if (dueDate && new Date(dueDate) <= new Date()) {
				return "awaiting_decision";
			}
		}
		return status;
	};

	const effectiveStatus = solicitation
		? getEffectiveStatus(
				solicitation.status,
				solicitation.publish_date,
				solicitation.due_date,
			)
		: null;

	// Handle state transitions
	const handleTransition = async (
		targetStatus: "draft" | "open" | "archived" | "cancelled" | "awarded",
		successMessage: string,
		publishDate?: string,
	) => {
		setIsTransitioning(true);
		try {
			if (targetStatus === "open" && !publishDate) {
				// Use publish endpoint for immediate publishing
				await publishMutation.mutateAsync({ id });
			} else {
				await transitionMutation.mutateAsync({ id, targetStatus, publishDate });
			}
			await queryClient.invalidateQueries({
				queryKey: trpc.solicitations.get.queryKey({ id }),
			});
			toast.success(successMessage);
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to update solicitation",
			);
		} finally {
			setIsTransitioning(false);
		}
	};

	// Handle closing bidding (sets due_date to now, creating virtual "awaiting_decision" state)
	const handleCloseBidding = async () => {
		setIsTransitioning(true);
		try {
			await closeBiddingMutation.mutateAsync({ id });
			await queryClient.invalidateQueries({
				queryKey: trpc.solicitations.get.queryKey({ id }),
			});
			toast.success("Bidding closed - awaiting decision");
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to close bidding",
			);
		} finally {
			setIsTransitioning(false);
		}
	};

	// Fetch bids for this solicitation
	const { data: bidsData, isLoading: bidsLoading } = useQuery(
		trpc.vendorManagement.bidSubmissions.list.queryOptions({
			solicitationId: id,
			includeWithdrawn: false,
		}),
	);

	// Fetch awards for this solicitation
	const { data: awardsData } = useQuery({
		...trpc.awards.bids.list.queryOptions({
			solicitationId: id,
			buyerOrgId: solicitation?.buyer_org_id ?? "",
		}),
		enabled: !!solicitation?.buyer_org_id,
	});

	// Fetch smart codes
	const { data: smartCodes } = useQuery(
		trpc.smartCodes.list.queryOptions({ solicitationId: id }),
	);

	// Fetch milestones
	const { data: milestones } = useQuery(
		trpc.milestones.list.queryOptions({ solicitationId: id }),
	);

	// Fetch contract periods
	const { data: contractPeriods } = useQuery(
		trpc.solicitations.listContractPeriods.queryOptions({ solicitationId: id }),
	);

	// Fetch solicitation forms
	const { data: solicitationForms } = useQuery(
		trpc.forms.solicitationForms.list.queryOptions({ solicitationId: id }),
	);

	// Fetch required documents (solicitation-specific)
	const { data: requiredDocs } = useQuery(
		trpc.forms.requestedDocuments.list.queryOptions({ solicitationId: id }),
	);

	// Fetch compliance checks (solicitation-specific)
	const { data: complianceChecks } = useQuery(
		trpc.complianceChecks.list.queryOptions({ solicitationId: id }),
	);

	// Fetch evaluation criteria
	const { data: evalCriteriaData } = useQuery(
		trpc.solicitations.listEvalCriteria.queryOptions({ solicitationId: id }),
	);

	// Fetch attachments/addendums
	const { data: attachmentsData } = useQuery(
		trpc.attachments.list.queryOptions({ solicitationId: id }),
	);

	// Get the selected organization ID for inherited data
	const selectedOrgId = solicitation?.subtier_org_id ?? solicitation?.member_org_id ?? solicitation?.buyer_org_id ?? "";

	// Fetch inherited organization data
	const { data: inheritedComplianceChecks } = useQuery(
		trpc.organizations.listComplianceChecksWithInheritance.queryOptions(
			{ organizationId: selectedOrgId },
			{ enabled: !!selectedOrgId },
		),
	);

	const { data: inheritedRequiredDocs } = useQuery(
		trpc.organizations.listRequiredDocsWithInheritance.queryOptions(
			{ organizationId: selectedOrgId },
			{ enabled: !!selectedOrgId },
		),
	);

	const { data: inheritedOrgForms } = useQuery(
		trpc.organizations.listOrgFormsWithInheritance.queryOptions(
			{ organizationId: selectedOrgId },
			{ enabled: !!selectedOrgId },
		),
	);

	const { data: orgEvalCriteria } = useQuery(
		trpc.organizations.listEvaluationCriteriaWithInheritance.queryOptions(
			{ organizationId: selectedOrgId },
			{ enabled: !!selectedOrgId },
		),
	);

	// Fetch point of contact info
	const { data: pointOfContact } = useQuery(
		trpc.members.get.queryOptions(
			{ userId: solicitation?.point_of_contact_id ?? "" },
			{ enabled: !!solicitation?.point_of_contact_id },
		),
	);

	// Map bids to expected format (score is now a weighted percentage 0-100)
	const solicitationBids = (bidsData ?? []).map((bid) => ({
		id: bid.id,
		solicitationId: bid.solicitation_id,
		vendorId: bid.vendor_email,
		vendorName: bid.vendor_name ?? "Unknown Vendor",
		vendorEmail: bid.vendor_display_email ?? null,
		status: bid.status,
		score: bid.score, // Weighted percentage 0-100
		hasMultipleRowScores: bid.hasMultipleRowScores ?? false, // For split criteria
		hasSplitCriteria: bid.hasSplitCriteria ?? false,
		submittedAt: bid.submitted_at,
	}));

	// Determine which evaluation criteria to display
	// If useOrgCriteria is true and org has criteria, show org criteria
	// Otherwise show solicitation-specific criteria
	const useOrgCriteria = evalCriteriaData?.useOrgCriteria ?? true;
	const hasOrgCriteria = orgEvalCriteria && orgEvalCriteria.length > 0;

	const displayCriteria = (useOrgCriteria && hasOrgCriteria)
		? (orgEvalCriteria ?? []).map(c => ({
				id: c.id,
				solicitationId: id,
				title: c.title,
				description: c.description ?? "",
				maxPoints: c.max_points,
				weight: Number(c.weight) || 0,
				sortOrder: c.sort_order ?? 0,
			}))
		: (evalCriteriaData?.criteria ?? []).map(c => ({
				id: c.id,
				solicitationId: id,
				title: c.title,
				description: c.description ?? "",
				maxPoints: c.max_points,
				weight: Number(c.weight) || 0,
				sortOrder: c.sort_order ?? 0,
			}));

	// Calculate totals
	const totalPoints = displayCriteria.reduce((sum, c) => sum + c.maxPoints, 0);
	const totalWeight = displayCriteria.reduce((sum, c) => sum + c.weight, 0);

	// Parse sheets for tabular data export
	const sheets = (solicitation?.sheets ?? []) as SheetDefinition[];
	const hasSheets = sheets.length > 0;
	const splitEvaluationBySheet = (solicitation?.split_evaluation_by_sheet ?? null) as number | null;

	// Bids state for comparison
	const [bidSortField, setBidSortField] = useState<"score" | "date">(
		"score",
	);
	const [bidSortDirection, setBidSortDirection] = useState<"asc" | "desc">(
		"desc",
	);

	// Sort bids
	const displayedBids = solicitationBids
		.sort((a, b) => {
			let comparison = 0;
			switch (bidSortField) {
				case "score":
					comparison = (a.score ?? 0) - (b.score ?? 0);
					break;
				case "date":
					comparison =
						(a.submittedAt ? new Date(a.submittedAt).getTime() : 0) -
						(b.submittedAt ? new Date(b.submittedAt).getTime() : 0);
					break;
			}
			return bidSortDirection === "desc" ? -comparison : comparison;
		});

	// Calculate rankings
	const rankedBids = [...solicitationBids]
		.sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
		.map((bid, index) => ({ ...bid, rank: index + 1 }));

	const getBidRank = (bidId: string) =>
		rankedBids.find((b) => b.id === bidId)?.rank;

	// Handle attachment upload
	const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;

		setIsUploadingAttachment(true);
		try {
			for (const file of Array.from(files)) {
				// Get signed upload URL
				const { signedUrl, path } =
					await createSignedUploadUrlMutation.mutateAsync({
						solicitationId: id,
						fileName: file.name,
					});

				// Upload file directly to storage
				const uploadResponse = await fetch(signedUrl, {
					method: "PUT",
					headers: {
						"Content-Type": file.type || "application/octet-stream",
					},
					body: file,
				});

				if (!uploadResponse.ok) {
					throw new Error(`Failed to upload ${file.name}`);
				}

				// Record attachment in database
				await uploadAttachmentMutation.mutateAsync({
					solicitationId: id,
					title: file.name,
					filePath: path,
					fileSize: file.size,
					mimeType: file.type || null,
				});
			}

			// Refresh attachments list
			queryClient.invalidateQueries({
				queryKey: trpc.attachments.list.queryKey({ solicitationId: id }),
			});
			toast.success(
				files.length === 1
					? "File uploaded successfully"
					: `${files.length} files uploaded successfully`,
			);
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to upload file",
			);
		} finally {
			setIsUploadingAttachment(false);
			// Reset file input
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	// Confirmation modal states
	const [publishDialogOpen, setPublishDialogOpen] = useState(false);
	const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
	const [backToDraftDialogOpen, setBackToDraftDialogOpen] = useState(false);
	const [closeBiddingDialogOpen, setCloseBiddingDialogOpen] = useState(false);
	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

	// Award modal state
	const [awardDialogOpen, setAwardDialogOpen] = useState(false);
	const [selectedVendors, setSelectedVendors] = useState<Set<string>>(
		new Set(),
	);
	const [pendingAwardForms, setPendingAwardForms] = useState<PendingAwardForm[]>([]);
	const [awardForm, setAwardForm] = useState({
		contractStartDate: "",
		contractEndDate: "",
		sendEmail: true,
	});

	// Form editor state for creating award forms
	const [awardFormEditorOpen, setAwardFormEditorOpen] = useState(false);
	const [editingAwardFormIndex, setEditingAwardFormIndex] = useState<number | null>(null);

	// Pending attachments for award (uploaded files before award is created)
	const [pendingAwardAttachments, setPendingAwardAttachments] = useState<
		{ name: string; storagePath: string; size: number }[]
	>([]);
	const [isUploadingAwardAttachment, setIsUploadingAwardAttachment] = useState(false);
	const awardAttachmentInputRef = useRef<HTMLInputElement>(null);

	// Add award form mutation
	const addAwardFormMutation = useMutation(
		trpc.awards.forms.add.mutationOptions(),
	);

	// Create form template mutation (for creating new form templates)
	const createFormTemplateMutation = useMutation(
		trpc.forms.templates.create.mutationOptions(),
	);

	// Upload contract document mutation
	const uploadContractDocumentMutation = useMutation(
		trpc.awards.documents.upload.mutationOptions(),
	);

	// Save widgets mutation (for saving form field definitions after creating template)
	const saveWidgetsMutation = useMutation(
		trpc.pdfTemplates.saveWidgets.mutationOptions(),
	);

	// Send award email mutation
	const sendAwardEmailMutation = useMutation(
		trpc.vendorManagement.awardEmails.sendOne.mutationOptions(),
	);

	const handleOpenAwardDialog = () => {
		// Pre-select the top-ranked bid
		const topBid = rankedBids[0];
		if (topBid) {
			setSelectedVendors(new Set([topBid.id]));
		}
		setPendingAwardForms([]);
		setPendingAwardAttachments([]);
		setAwardForm({
			contractStartDate: "",
			contractEndDate: "",
			sendEmail: true,
		});
		setAwardDialogOpen(true);
	};

	const handleAwardAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file || !solicitation) return;

		setIsUploadingAwardAttachment(true);
		try {
			// Get signed upload URL
			const { signedUrl, path } = await createSignedUploadUrlMutation.mutateAsync({
				fileName: file.name,
				contentType: file.type,
				solicitationId: solicitation.id,
				category: "award_document",
			});

			// Upload file directly to storage
			const uploadResponse = await fetch(signedUrl, {
				method: "PUT",
				headers: { "Content-Type": file.type },
				body: file,
			});

			if (!uploadResponse.ok) {
				throw new Error("Failed to upload file");
			}

			// Add to pending attachments
			setPendingAwardAttachments((prev) => [
				...prev,
				{ name: file.name, storagePath: path, size: file.size },
			]);

			toast.success("Document uploaded");
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Failed to upload document");
		} finally {
			setIsUploadingAwardAttachment(false);
			if (awardAttachmentInputRef.current) {
				awardAttachmentInputRef.current.value = "";
			}
		}
	};

	const handleRemoveAwardAttachment = (index: number) => {
		setPendingAwardAttachments((prev) => prev.filter((_, i) => i !== index));
	};

	const handleAddAwardForm = () => {
		setEditingAwardFormIndex(null);
		// Close award dialog before opening form editor to avoid z-index issues
		setAwardDialogOpen(false);
		setAwardFormEditorOpen(true);
	};

	const handleEditAwardForm = (index: number) => {
		setEditingAwardFormIndex(index);
		// Close award dialog before opening form editor to avoid z-index issues
		setAwardDialogOpen(false);
		setAwardFormEditorOpen(true);
	};

	const handleRemoveAwardForm = (index: number) => {
		setPendingAwardForms((prev) => prev.filter((_, i) => i !== index));
	};

	const handleSaveAwardForm = (formData: {
		name: string;
		description: string;
		pdfStoragePath: string | null;
		widgets: WidgetData[];
	}) => {
		if (editingAwardFormIndex !== null) {
			// Update existing form
			setPendingAwardForms((prev) =>
				prev.map((form, i) =>
					i === editingAwardFormIndex
						? { ...form, ...formData }
						: form
				)
			);
		} else {
			// Add new form
			const newForm: PendingAwardForm = {
				id: crypto.randomUUID(),
				name: formData.name,
				description: formData.description,
				pdfStoragePath: formData.pdfStoragePath,
				widgets: formData.widgets,
				isRequired: true,
			};
			setPendingAwardForms((prev) => [...prev, newForm]);
		}
		setAwardFormEditorOpen(false);
		setEditingAwardFormIndex(null);
		// Reopen the award dialog
		setAwardDialogOpen(true);
	};

	const handleToggleVendorSelection = (bidId: string) => {
		setSelectedVendors((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(bidId)) {
				newSet.delete(bidId);
			} else {
				newSet.add(bidId);
			}
			return newSet;
		});
	};

	const [isAwarding, setIsAwarding] = useState(false);

	const handleSubmitAward = async () => {
		if (selectedVendors.size === 0) {
			toast.error("Please select at least one vendor to award");
			return;
		}
		if (!awardForm.contractStartDate || !awardForm.contractEndDate) {
			toast.error("Please enter contract start and end dates");
			return;
		}

		setIsAwarding(true);
		try {
			// First, create all form templates from pending forms
			const createdTemplates: { templateId: string; form: PendingAwardForm }[] = [];
			for (const pendingForm of pendingAwardForms) {
				if (!pendingForm.pdfStoragePath) continue;

				const template = await createFormTemplateMutation.mutateAsync({
					title: pendingForm.name,
					description: pendingForm.description || undefined,
					pdfStoragePath: pendingForm.pdfStoragePath,
					fieldConfig: [],
				});

				// Save the widgets to the newly created template
				if (pendingForm.widgets.length > 0) {
					await saveWidgetsMutation.mutateAsync({
						templateId: template.id,
						widgets: pendingForm.widgets.map((w) => ({
							field_name: w.field_name,
							field_label: w.field_label,
							field_type: w.field_type,
							page_number: w.page_number,
							x: w.x,
							y: w.y,
							width: w.width,
							height: w.height,
							is_required: w.is_required,
							placeholder: w.placeholder,
							description: w.description,
							options: w.options,
							font_size: w.font_size,
							tab_order: w.tab_order,
						})),
					});
				}

				createdTemplates.push({ templateId: template.id, form: pendingForm });
			}

			// Award each selected vendor
			for (const bidId of selectedVendors) {
				const bid = rankedBids.find((b) => b.id === bidId);
				if (!bid) continue;

				const award = await createAwardMutation.mutateAsync({
					solicitationId: id,
					buyerOrgId: solicitation!.buyer_org_id,
					vendorEmail: bid.vendorId, // vendorId is actually the vendor_email
					contractStartDate: awardForm.contractStartDate,
					contractEndDate: awardForm.contractEndDate,
				});

				// Link form templates to this award
				if (createdTemplates.length > 0 && award?.id) {
					let sortOrder = 0;
					for (const { templateId, form } of createdTemplates) {
						await addAwardFormMutation.mutateAsync({
							bidAwardId: award.id,
							formTemplateId: templateId,
							title: form.name,
							description: form.description || null,
							isRequired: form.isRequired,
							sortOrder: sortOrder++,
						});
					}
				}

				// Upload award attachments
				if (pendingAwardAttachments.length > 0 && award?.id) {
					for (const attachment of pendingAwardAttachments) {
						await uploadContractDocumentMutation.mutateAsync({
							solicitationId: id,
							buyerOrgId: solicitation!.buyer_org_id,
							bidAwardId: award.id,
							documentType: "award",
							title: attachment.name,
							filePath: attachment.storagePath,
							fileSize: attachment.size,
							originalFilename: attachment.name,
						});
					}
				}

				// Send award notification email if requested
				if (awardForm.sendEmail && user?.id) {
					await sendAwardEmailMutation.mutateAsync({
						solicitationId: id,
						buyerOrgId: solicitation!.buyer_org_id,
						vendorEmail: bid.vendorId,
						isAwarded: true,
						sentBy: user.id,
					});
				}
			}

			// Refresh solicitation and awards data
			await queryClient.invalidateQueries({
				queryKey: trpc.solicitations.get.queryKey({ id }),
			});
			await queryClient.invalidateQueries({
				queryKey: ["awards", "bids", "list"],
			});

			const vendorCount = selectedVendors.size;
			toast.success(
				`Contract awarded to ${vendorCount} vendor${vendorCount > 1 ? "s" : ""}!`,
			);
			setAwardDialogOpen(false);
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to create award",
			);
		} finally {
			setIsAwarding(false);
		}
	};

	const isLoading = solicitationLoading || bidsLoading;

	if (isLoading) {
		return (
			<DashboardLayout>
				<div className="flex items-center justify-center h-64">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</DashboardLayout>
		);
	}

	if (!solicitation) {
		return (
			<DashboardLayout>
				<div className="text-center py-12">
					<h2 className="text-2xl font-bold">Solicitation not found</h2>
					<p className="text-muted-foreground mt-2">
						The solicitation you're looking for doesn't exist.
					</p>
					<Button asChild className="mt-4">
						<Link to="/solicitations">Back to Solicitations</Link>
					</Button>
				</div>
			</DashboardLayout>
		);
	}

	// Format file size for display
	const formatFileSize = (bytes: number): string => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	return (
		<DashboardLayout>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="space-y-1">
						<div className="flex items-center gap-2">
							<Button variant="ghost" size="icon" asChild>
								<Link to="/solicitations">
									<ArrowLeft className="h-4 w-4" />
								</Link>
							</Button>
							<Badge className={getStatusColor(effectiveStatus || solicitation.status)}>
								{getStatusLabel(effectiveStatus || solicitation.status)}
							</Badge>
						</div>
						<h1 className="text-3xl font-bold tracking-tight">
							{solicitation.title}
						</h1>
						<p className="text-muted-foreground max-w-3xl">
							{solicitation.description}
						</p>
					</div>
					{hasPermission("edit") && (
						<div className="flex gap-2 flex-wrap">
							{/* Draft: Edit, Publish/Schedule, Archive */}
							{effectiveStatus === "draft" && (
								<>
									<Button variant="outline" asChild>
										<Link to="/solicitations/$id/edit" params={{ id }}>
											<Edit className="mr-2 h-4 w-4" />
											Edit
										</Link>
									</Button>
									{(() => {
										const isScheduled = solicitation.publish_date && new Date(solicitation.publish_date) > new Date();
										return (
											<Button
												onClick={() => setPublishDialogOpen(true)}
												disabled={isTransitioning}
											>
												{isTransitioning ? (
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												) : isScheduled ? (
													<Clock className="mr-2 h-4 w-4" />
												) : (
													<Send className="mr-2 h-4 w-4" />
												)}
												{isScheduled ? "Schedule" : "Publish"}
											</Button>
										);
									})()}
									<Button
										variant="outline"
										onClick={() => setArchiveDialogOpen(true)}
										disabled={isTransitioning}
									>
										<Archive className="mr-2 h-4 w-4" />
										Archive
									</Button>
								</>
							)}

							{/* Scheduled: Update, Back to Draft, Publish Now, Archive */}
							{effectiveStatus === "scheduled" && (
								<>
									<Button variant="outline" asChild>
										<Link to="/solicitations/$id/edit" params={{ id }}>
											<Edit className="mr-2 h-4 w-4" />
											Update
										</Link>
									</Button>
									<Button
										variant="outline"
										onClick={() => setBackToDraftDialogOpen(true)}
										disabled={isTransitioning}
									>
										<ArrowLeft className="mr-2 h-4 w-4" />
										Back to Draft
									</Button>
									<Button
										onClick={() => setPublishDialogOpen(true)}
										disabled={isTransitioning}
									>
										{isTransitioning ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<Play className="mr-2 h-4 w-4" />
										)}
										Publish Now
									</Button>
									<Button
										variant="outline"
										onClick={() => setArchiveDialogOpen(true)}
										disabled={isTransitioning}
									>
										<Archive className="mr-2 h-4 w-4" />
										Archive
									</Button>
								</>
							)}

							{/* Open: Copy Link, Update, Close Bidding, Cancel */}
							{effectiveStatus === "open" && (
								<>
									{buyerOrg?.slug && (
										<Button
											variant="outline"
											onClick={() => {
												const publicUrl = `${window.location.origin}/public/${buyerOrg.slug}/solicitations/${id}`;
												navigator.clipboard.writeText(publicUrl);
												toast.success("Public link copied to clipboard");
											}}
										>
											<Link2 className="mr-2 h-4 w-4" />
											Copy Link
										</Button>
									)}
									<Button variant="outline" asChild>
										<Link to="/solicitations/$id/edit" params={{ id }}>
											<Edit className="mr-2 h-4 w-4" />
											Update
										</Link>
									</Button>
									<Button
										onClick={() => setCloseBiddingDialogOpen(true)}
										disabled={isTransitioning}
									>
										{isTransitioning ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<Lock className="mr-2 h-4 w-4" />
										)}
										Close Bidding
									</Button>
									<Button
										variant="destructive"
										onClick={() => setCancelDialogOpen(true)}
										disabled={isTransitioning}
									>
										<Ban className="mr-2 h-4 w-4" />
										Cancel
									</Button>
								</>
							)}

							{/* Awaiting Decision: Award, Cancel */}
							{effectiveStatus === "awaiting_decision" && (
								<>
									<Button onClick={handleOpenAwardDialog}>
										<Trophy className="mr-2 h-4 w-4" />
										Award
									</Button>
									<Button
										variant="destructive"
										onClick={() => setCancelDialogOpen(true)}
										disabled={isTransitioning}
									>
										<Ban className="mr-2 h-4 w-4" />
										Cancel
									</Button>
								</>
							)}

							{/* Awarded: Can add more awards */}
							{effectiveStatus === "awarded" && (
								<Button onClick={handleOpenAwardDialog}>
									<Trophy className="mr-2 h-4 w-4" />
									Add Award
								</Button>
							)}

							{/* Archived, Cancelled: No action buttons (terminal states) */}
						</div>
					)}
				</div>

				{/* Quick Stats */}
				<div className="grid gap-4 md:grid-cols-4">
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Calendar className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">Due Date</span>
							</div>
							<p className="text-2xl font-bold mt-1">
								{solicitation.due_date ? new Date(solicitation.due_date).toLocaleDateString() : "TBD"}
							</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Users className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Bids Received
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">{solicitationBids.length}</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<FileText className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Attachments
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">
								{attachmentsData?.length ?? 0}
							</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Clock className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Milestones
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">
								{milestones?.length ?? 0}
							</p>
						</CardContent>
					</Card>
				</div>

				{/* Tabs */}
				<Tabs value={activeTab} onValueChange={setActiveTab}>
					<TabsList>
						<TabsTrigger value="overview">Overview</TabsTrigger>
						<TabsTrigger value="evaluation">Evaluation</TabsTrigger>
						<TabsTrigger value="bids">
							Bids ({solicitationBids.length})
						</TabsTrigger>
						<TabsTrigger value="requirements">Requirements</TabsTrigger>
						<TabsTrigger value="attachments">Attachments</TabsTrigger>
						{hasSheets && <TabsTrigger value="sheets">Sheets</TabsTrigger>}
						<TabsTrigger value="qa">Q&A</TabsTrigger>
						<TabsTrigger value="awards">Awards</TabsTrigger>
						<TabsTrigger value="timeline">Timeline</TabsTrigger>
					</TabsList>

					<TabsContent value="overview" className="space-y-6">
						<div className="grid gap-6 md:grid-cols-2">
							{/* Details Card */}
							<Card>
								<CardHeader>
									<CardTitle>Solicitation Details</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="flex items-start gap-3">
										<FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
										<div>
											<p className="text-sm text-muted-foreground">Type</p>
											<p className="font-medium">{solicitation.type || "Not specified"}</p>
										</div>
									</div>
									<div className="flex items-start gap-3">
										<Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
										<div>
											<p className="text-sm text-muted-foreground">Publish Date</p>
											<p className="font-medium">
												{solicitation.publish_date
													? new Date(solicitation.publish_date).toLocaleString()
													: "Not scheduled"}
											</p>
										</div>
									</div>
									<div className="flex items-start gap-3">
										<Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
										<div>
											<p className="text-sm text-muted-foreground">Due Date</p>
											<p className="font-medium">
												{solicitation.due_date
													? new Date(solicitation.due_date).toLocaleString()
													: "Not set"}
											</p>
										</div>
									</div>
									<div className="flex items-start gap-3">
										<Globe className="h-5 w-5 text-muted-foreground mt-0.5" />
										<div>
											<p className="text-sm text-muted-foreground">Visibility</p>
											<p className="font-medium">{solicitation.is_public ? "Public" : "Private"}</p>
										</div>
									</div>
									<div className="flex items-start gap-3">
										<Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
										<div>
											<p className="text-sm text-muted-foreground">Created</p>
											<p className="font-medium">
												{new Date(solicitation.created_at).toLocaleDateString()}
											</p>
										</div>
									</div>
								</CardContent>
							</Card>

							{/* Point of Contact Card */}
							<Card>
								<CardHeader>
									<CardTitle>Point of Contact</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									{pointOfContact ? (
										<>
											<div className="flex items-start gap-3">
												<User className="h-5 w-5 text-muted-foreground mt-0.5" />
												<div>
													<p className="text-sm text-muted-foreground">Name</p>
													<p className="font-medium">
														{pointOfContact.profiles?.first_name} {pointOfContact.profiles?.last_name}
													</p>
												</div>
											</div>
											{pointOfContact.profiles?.email && (
												<div className="flex items-start gap-3">
													<Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<p className="text-sm text-muted-foreground">Email</p>
														<p className="font-medium">{pointOfContact.profiles.email}</p>
													</div>
												</div>
											)}
										</>
									) : (
										<p className="text-muted-foreground">No point of contact assigned</p>
									)}
								</CardContent>
							</Card>
						</div>

						{/* Smart Codes */}
						{smartCodes && smartCodes.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle>Smart Codes</CardTitle>
									<CardDescription>Classification codes for this solicitation</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="flex flex-wrap gap-2">
										{smartCodes.map((code) => (
											<Badge key={code.id} variant="secondary">
												{code.value}
											</Badge>
										))}
									</div>
								</CardContent>
							</Card>
						)}

						{/* Contract Periods */}
						{contractPeriods && contractPeriods.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle>Contract Periods</CardTitle>
									<CardDescription>Performance periods for this contract</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="space-y-3">
										{contractPeriods.map((period) => (
											<div key={period.id} className="flex items-center justify-between p-3 border rounded-lg">
												<span className="font-medium">{period.name}</span>
												<span className="text-sm text-muted-foreground">
													{period.start_date ? new Date(period.start_date).toLocaleDateString() : "TBD"}
													{" - "}
													{period.end_date ? new Date(period.end_date).toLocaleDateString() : "TBD"}
												</span>
											</div>
										))}
									</div>
								</CardContent>
							</Card>
						)}

						{/* Description */}
						{solicitation.description && (
							<Card>
								<CardHeader>
									<CardTitle>Description</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-muted-foreground whitespace-pre-wrap">{solicitation.description}</p>
								</CardContent>
							</Card>
						)}
					</TabsContent>

					<TabsContent value="evaluation" className="space-y-6">
						{/* Source indicator */}
						{hasOrgCriteria && (
							<div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
								<Building2 className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									{useOrgCriteria
										? "Using evaluation criteria inherited from organization"
										: "Using solicitation-specific evaluation criteria"}
								</span>
							</div>
						)}

						{/* Evaluation Rubric */}
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Target className="h-5 w-5" />
									Evaluation Rubric
								</CardTitle>
								<CardDescription>
									Criteria used to evaluate bids. Total points: {totalPoints}.
								</CardDescription>
							</CardHeader>
							<CardContent>
								{/* Criteria list */}
								<div className="space-y-3">
									{displayCriteria
										.sort((a, b) => a.sortOrder - b.sortOrder)
										.map((criterion) => (
											<div
												key={criterion.id}
												className="flex items-start gap-3 p-4 border rounded-lg bg-muted/30"
											>
												<div className="flex-1">
													<div className="flex items-center justify-between">
														<h4 className="font-medium">{criterion.title}</h4>
														<Badge variant="outline" className="ml-2">
															{criterion.maxPoints} pts
														</Badge>
													</div>
													{criterion.description && (
														<p className="text-sm text-muted-foreground mt-1">
															{criterion.description}
														</p>
													)}
												</div>
											</div>
										))}
									{displayCriteria.length === 0 && (
										<div className="text-center py-8 text-muted-foreground">
											<Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
											<p>No evaluation criteria defined.</p>
										</div>
									)}
								</div>
							</CardContent>
						</Card>

						{/* Best Value Scoring Weights */}
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Scale className="h-5 w-5" />
									Best Value Scoring Weights
								</CardTitle>
								<CardDescription>
									Relative importance of evaluation factors. Total:{" "}
									{totalWeight}%.
								</CardDescription>
							</CardHeader>
							<CardContent>
								{/* Weight bars (read-only) */}
								<div className="space-y-4">
									{displayCriteria.map((criterion) => (
										<div key={criterion.id} className="space-y-1">
											<div className="flex items-center justify-between">
												<span className="text-sm font-medium">
													{criterion.title}
												</span>
												<span className="text-sm font-medium">
													{criterion.weight}%
												</span>
											</div>
											<Progress value={criterion.weight} />
										</div>
									))}
									{displayCriteria.length === 0 && (
										<div className="text-center py-8 text-muted-foreground">
											<Scale className="h-12 w-12 mx-auto mb-3 opacity-50" />
											<p>No scoring weights configured.</p>
										</div>
									)}
								</div>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="bids" className="space-y-4">
						{/* Bid Summary Cards */}
						<div className="grid gap-4 md:grid-cols-2">
							<Card>
								<CardContent className="pt-6">
									<div className="flex items-center gap-2">
										<Users className="h-4 w-4 text-muted-foreground" />
										<span className="text-sm text-muted-foreground">
											Total Bids
										</span>
									</div>
									<p className="text-2xl font-bold mt-1">
										{solicitationBids.length}
									</p>
								</CardContent>
							</Card>
							<Card>
								<CardContent className="pt-6">
									<div className="flex items-center gap-2">
										<Trophy className="h-4 w-4 text-muted-foreground" />
										<span className="text-sm text-muted-foreground">
											Top Score
										</span>
									</div>
									<p className="text-2xl font-bold mt-1">
										{rankedBids[0]?.hasMultipleRowScores
											? "Multiple"
											: rankedBids[0]?.score != null
												? `${rankedBids[0].score}%`
												: "-"}
									</p>
								</CardContent>
							</Card>
						</div>

						<Card>
							<CardHeader>
								<CardTitle>Bids</CardTitle>
								<CardDescription>
									Compare and evaluate all submitted bids
								</CardDescription>
							</CardHeader>
							<CardContent>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="w-12">Rank</TableHead>
											<TableHead>Vendor</TableHead>
											<TableHead
												className="cursor-pointer hover:text-primary"
												onClick={() => {
													if (bidSortField === "date") {
														setBidSortDirection((d) =>
															d === "asc" ? "desc" : "asc",
														);
													} else {
														setBidSortField("date");
														setBidSortDirection("desc");
													}
												}}
											>
												<div className="flex items-center gap-1">
													Submitted
													<ArrowUpDown className="h-3 w-3" />
												</div>
											</TableHead>
											<TableHead>Status</TableHead>
											<TableHead
												className="cursor-pointer hover:text-primary"
												onClick={() => {
													if (bidSortField === "score") {
														setBidSortDirection((d) =>
															d === "asc" ? "desc" : "asc",
														);
													} else {
														setBidSortField("score");
														setBidSortDirection("desc");
													}
												}}
											>
												<div className="flex items-center gap-1">
													Score
													<ArrowUpDown className="h-3 w-3" />
												</div>
											</TableHead>
											<TableHead className="text-right">Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{displayedBids.map((bid) => {
											const rank = getBidRank(bid.id);

											return (
												<TableRow key={bid.id}>
													<TableCell>
														{rank && !bid.hasMultipleRowScores && bid.score !== null && (
															<span>#{rank}</span>
														)}
													</TableCell>
													<TableCell>
														<span className="font-medium">
															{bid.vendorName}
														</span>
													</TableCell>
													<TableCell>
														{bid.submittedAt
															? new Date(bid.submittedAt).toLocaleDateString()
															: "-"}
													</TableCell>
													<TableCell>
														<Badge className={getBidStatusColor(bid.status)}>
															{bid.status.replace("_", " ")}
														</Badge>
													</TableCell>
													<TableCell>
														{bid.hasMultipleRowScores ? (
															<span
																className="text-muted-foreground cursor-help"
																title="Scores are per line item. Click to view details."
															>
																Multiple
															</span>
														) : bid.score !== null ? (
															<span>{bid.score}%</span>
														) : (
															"-"
														)}
													</TableCell>
													<TableCell className="text-right">
														<Button variant="outline" size="sm" asChild>
															<Link
																to="/solicitations/bids/$bidId"
																params={{ bidId: bid.id }}
															>
																<Eye className="mr-1 h-3 w-3" />
																Review
															</Link>
														</Button>
													</TableCell>
												</TableRow>
											);
										})}
										{displayedBids.length === 0 && (
											<TableRow>
												<TableCell
													colSpan={6}
													className="text-center py-8 text-muted-foreground"
												>
													No bids received yet
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="requirements" className="space-y-4">
						{/* Vendor Forms */}
						<Card>
							<CardHeader>
								<CardTitle>Vendor Forms</CardTitle>
								<CardDescription>
									Fillable PDF forms vendors must complete with their submission
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-3">
									{/* Inherited forms from organization */}
									{inheritedOrgForms && inheritedOrgForms.length > 0 && (
										<div className="mb-4">
											<div className="flex items-center gap-2 mb-2">
												<Building2 className="h-4 w-4 text-muted-foreground" />
												<span className="text-sm text-muted-foreground font-medium">
													Inherited from Organization
												</span>
											</div>
											{inheritedOrgForms.map((form) => (
												<div
													key={form.id}
													className="flex items-start gap-3 p-4 border rounded-lg bg-muted/20 mb-2"
												>
													<FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div className="flex-1">
														<div className="flex items-center gap-2">
															<span className="font-medium">{form.title}</span>
															<Badge
																variant={form.is_required ? "default" : "outline"}
															>
																{form.is_required ? "Required" : "Optional"}
															</Badge>
															<Badge variant="secondary">Inherited</Badge>
														</div>
														{form.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{form.description}
															</p>
														)}
													</div>
													<FormDownloadButton
														pdfPath={form.form_templates?.pdf_path}
														formTemplateId={form.form_template_id}
														pdfStoragePath={form.form_templates?.pdf_storage_path}
														title={form.title}
													/>
												</div>
											))}
										</div>
									)}

									{/* Solicitation-specific forms */}
									{solicitationForms && solicitationForms.length > 0 ? (
										<>
											{inheritedOrgForms && inheritedOrgForms.length > 0 && (
												<span className="text-sm text-muted-foreground font-medium mb-2 block">
													Solicitation-Specific
												</span>
											)}
											{solicitationForms.map((form) => (
												<div
													key={form.id}
													className="flex items-start gap-3 p-4 border rounded-lg"
												>
													<FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div className="flex-1">
														<div className="flex items-center gap-2">
															<span className="font-medium">{form.title ?? "Untitled Form"}</span>
															<Badge
																variant={form.is_required ? "default" : "outline"}
															>
																{form.is_required ? "Required" : "Optional"}
															</Badge>
														</div>
														{form.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{form.description}
															</p>
														)}
													</div>
													<FormDownloadButton
														pdfPath={form.form_templates?.pdf_path}
														formTemplateId={form.form_template_id}
														pdfStoragePath={form.form_templates?.pdf_storage_path}
														title={form.title ?? "Form"}
													/>
												</div>
											))}
										</>
									) : (
										(!inheritedOrgForms || inheritedOrgForms.length === 0) && (
											<div className="text-center py-8 text-muted-foreground">
												<FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
												<p>No vendor forms configured for this solicitation.</p>
											</div>
										)
									)}
								</div>
							</CardContent>
						</Card>

						{/* Required Documents */}
						<Card>
							<CardHeader>
								<CardTitle>Required Documents</CardTitle>
								<CardDescription>
									Documents vendors must upload with their submission
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-3">
									{/* Inherited documents */}
									{inheritedRequiredDocs && inheritedRequiredDocs.length > 0 && (
										<div className="mb-4">
											<div className="flex items-center gap-2 mb-2">
												<Building2 className="h-4 w-4 text-muted-foreground" />
												<span className="text-sm text-muted-foreground font-medium">
													Inherited from Organization
												</span>
											</div>
											{inheritedRequiredDocs.map((doc) => (
												<div
													key={doc.id}
													className="flex items-start gap-3 p-4 border rounded-lg bg-muted/20 mb-2"
												>
													<Upload className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div className="flex-1">
														<div className="flex items-center gap-2">
															<span className="font-medium">{doc.title}</span>
															<Badge variant={doc.is_required ? "default" : "outline"}>
																{doc.is_required ? "Required" : "Optional"}
															</Badge>
															<Badge variant="secondary">Inherited</Badge>
														</div>
														{doc.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{doc.description}
															</p>
														)}
													</div>
												</div>
											))}
										</div>
									)}

									{/* Solicitation-specific documents */}
									{requiredDocs && requiredDocs.length > 0 ? (
										<>
											{inheritedRequiredDocs && inheritedRequiredDocs.length > 0 && (
												<span className="text-sm text-muted-foreground font-medium mb-2 block">
													Solicitation-Specific
												</span>
											)}
											{requiredDocs.map((doc) => (
												<div
													key={doc.id}
													className="flex items-start gap-3 p-4 border rounded-lg"
												>
													<Upload className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div className="flex-1">
														<div className="flex items-center gap-2">
															<span className="font-medium">{doc.title}</span>
															<Badge variant={doc.is_required ? "default" : "outline"}>
																{doc.is_required ? "Required" : "Optional"}
															</Badge>
														</div>
														{doc.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{doc.description}
															</p>
														)}
													</div>
												</div>
											))}
										</>
									) : (
										(!inheritedRequiredDocs || inheritedRequiredDocs.length === 0) && (
											<div className="text-center py-8 text-muted-foreground">
												<Upload className="h-12 w-12 mx-auto mb-3 opacity-50" />
												<p>No required documents configured for this solicitation.</p>
											</div>
										)
									)}
								</div>
							</CardContent>
						</Card>

						{/* Compliance Checks */}
						<Card>
							<CardHeader>
								<CardTitle>Compliance Checks</CardTitle>
								<CardDescription>
									Requirements vendors must meet (certifications, registrations,
									etc.)
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-3">
									{/* Inherited from Organization */}
									{inheritedComplianceChecks && inheritedComplianceChecks.length > 0 && (
										<div className="mb-4">
											<div className="flex items-center gap-2 mb-2">
												<Building2 className="h-4 w-4 text-muted-foreground" />
												<span className="text-sm text-muted-foreground font-medium">
													Inherited from Organization
												</span>
											</div>
											{inheritedComplianceChecks.map((check) => (
												<div
													key={check.id}
													className="flex items-start gap-3 p-4 border rounded-lg bg-muted/20 mb-2"
												>
													<ClipboardCheck className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div className="flex-1">
														<div className="flex items-center gap-2">
															<span className="font-medium">{check.title}</span>
															<Badge
																variant={check.is_required ? "default" : "outline"}
															>
																{check.is_required ? "Required" : "Optional"}
															</Badge>
															<Badge variant="secondary">Inherited</Badge>
														</div>
														{check.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{check.description}
															</p>
														)}
													</div>
												</div>
											))}
										</div>
									)}

									{/* Solicitation-Specific */}
									{complianceChecks && complianceChecks.length > 0 ? (
										<div>
											{inheritedComplianceChecks && inheritedComplianceChecks.length > 0 && (
												<span className="text-sm text-muted-foreground font-medium mb-2 block">
													Solicitation-Specific
												</span>
											)}
											{complianceChecks.map((check) => (
												<div
													key={check.id}
													className="flex items-start gap-3 p-4 border rounded-lg mb-2"
												>
													<ClipboardCheck className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div className="flex-1">
														<div className="flex items-center gap-2">
															<span className="font-medium">{check.title}</span>
															<Badge
																variant={check.is_required ? "default" : "outline"}
															>
																{check.is_required ? "Required" : "Optional"}
															</Badge>
														</div>
														{check.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{check.description}
															</p>
														)}
													</div>
												</div>
											))}
										</div>
									) : (
										(!inheritedComplianceChecks || inheritedComplianceChecks.length === 0) && (
											<div className="text-center py-8 text-muted-foreground">
												<ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
												<p>No compliance checks configured for this solicitation.</p>
											</div>
										)
									)}
								</div>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="attachments" className="space-y-4">
						<Card>
							<CardHeader className="flex flex-row items-center justify-between">
								<div>
									<CardTitle>Solicitation Attachments</CardTitle>
									<CardDescription>
										Reference documents and supporting materials for this
										solicitation
									</CardDescription>
								</div>
								{hasPermission("edit") && effectiveStatus === "draft" && (
									<>
										<input
											ref={fileInputRef}
											type="file"
											multiple
											className="hidden"
											onChange={handleAttachmentUpload}
											accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.png,.jpg,.jpeg"
										/>
										<Button
											onClick={() => fileInputRef.current?.click()}
											disabled={isUploadingAttachment}
										>
											{isUploadingAttachment ? (
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											) : (
												<Upload className="mr-2 h-4 w-4" />
											)}
											{isUploadingAttachment ? "Uploading..." : "Upload Document"}
										</Button>
									</>
								)}
							</CardHeader>
							<CardContent>
								{attachmentsData && attachmentsData.length > 0 ? (
									<div className="space-y-2">
										{attachmentsData.map((file) => (
											<AttachmentRow key={file.id} file={file} formatFileSize={formatFileSize} />
										))}
									</div>
								) : (
									<div className="text-center py-8 text-muted-foreground">
										<FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
										<p>No attachments have been added to this solicitation.</p>
									</div>
								)}
							</CardContent>
						</Card>
					</TabsContent>

					{hasSheets && (
						<TabsContent value="sheets" className="space-y-4">
							<SolicitationSheets
								solicitationId={id}
								solicitationTitle={solicitation.title}
								sheets={sheets}
								splitEvaluationBySheet={splitEvaluationBySheet}
							/>
						</TabsContent>
					)}

					<TabsContent value="qa" className="space-y-4">
						<SolicitationQA
							solicitationId={id}
							buyerOrgId={solicitation.buyer_org_id}
							mode="buyer"
						/>
					</TabsContent>

					<TabsContent value="awards" className="space-y-4">
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<div>
										<CardTitle className="flex items-center gap-2">
											<Trophy className="h-5 w-5" />
											Awards
										</CardTitle>
										<CardDescription>
											Vendors awarded this contract
										</CardDescription>
									</div>
									{(effectiveStatus === "awaiting_decision" || effectiveStatus === "awarded") && (
										<Button onClick={handleOpenAwardDialog}>
											<Trophy className="mr-2 h-4 w-4" />
											{awardsData && awardsData.length > 0 ? "Add Award" : "Award Contract"}
										</Button>
									)}
								</div>
							</CardHeader>
							<CardContent>
								{!awardsData || awardsData.length === 0 ? (
									<div className="text-center py-8 text-muted-foreground">
										<Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
										<p>No awards yet</p>
										{effectiveStatus === "awaiting_decision" && (
											<p className="text-sm mt-2">
												Click "Award Contract" to award this solicitation to one or more vendors
											</p>
										)}
									</div>
								) : (
									<div className="space-y-4">
										{awardsData.map((award) => (
											<Card key={award.id} className="border">
												<CardContent className="pt-4">
													<div className="flex items-start justify-between">
														<div>
															<div className="flex items-center gap-2">
																<span className="font-medium">
																	{award.vendor_name ?? "Unknown Vendor"}
																</span>
																<Badge variant={award.status === "active" ? "default" : "secondary"}>
																	{award.status}
																</Badge>
															</div>
															<p className="text-sm text-muted-foreground mt-1">
																{award.vendor_display_email ?? award.vendor_email}
															</p>
															<p className="text-xs text-muted-foreground mt-1">
																Awarded {new Date(award.awarded_at).toLocaleDateString()}
															</p>
														</div>
													</div>
												</CardContent>
											</Card>
										))}
									</div>
								)}
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="timeline" className="space-y-4">
						{/* Milestones */}
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Calendar className="h-5 w-5" />
									Milestones
								</CardTitle>
								<CardDescription>
									Key dates and deadlines for this solicitation
								</CardDescription>
							</CardHeader>
							<CardContent>
								{milestones && milestones.length > 0 ? (
									<div className="space-y-4">
										{milestones
											.sort((a, b) => {
												if (!a.date && !b.date) return 0;
												if (!a.date) return 1;
												if (!b.date) return -1;
												return new Date(a.date).getTime() - new Date(b.date).getTime();
											})
											.map((milestone, index, arr) => {
												const isPast = milestone.date && new Date(milestone.date) < new Date();
												const isUpcoming = milestone.date &&
													new Date(milestone.date) >= new Date() &&
													new Date(milestone.date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

												return (
													<div key={milestone.id} className="flex items-start gap-4">
														<div className="flex flex-col items-center">
															<div className={`w-3 h-3 rounded-full mt-1.5 ${
																isPast ? "bg-green-500" : isUpcoming ? "bg-yellow-500" : "bg-primary"
															}`} />
															{index < arr.length - 1 && (
																<div className="w-0.5 h-full bg-border flex-1 mt-1" />
															)}
														</div>
														<div className="flex-1 pb-4">
															<div className="flex items-center gap-2">
																<p className="font-medium">{milestone.name}</p>
																{isPast && (
																	<Badge variant="secondary" className="text-xs">Completed</Badge>
																)}
																{isUpcoming && (
																	<Badge variant="outline" className="text-xs border-yellow-500 text-yellow-700">Upcoming</Badge>
																)}
															</div>
															<p className="text-sm text-muted-foreground">
																{milestone.date
																	? new Date(milestone.date).toLocaleDateString(undefined, {
																			weekday: "long",
																			year: "numeric",
																			month: "long",
																			day: "numeric",
																		})
																	: "Date TBD"}
															</p>
															{milestone.description && (
																<p className="text-sm text-muted-foreground mt-1">
																	{milestone.description}
																</p>
															)}
														</div>
													</div>
												);
											})}
									</div>
								) : (
									<div className="text-center py-8 text-muted-foreground">
										<Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
										<p>No milestones have been set for this solicitation.</p>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Key Dates Summary */}
						<Card>
							<CardHeader>
								<CardTitle>Key Dates</CardTitle>
								<CardDescription>
									Important dates for this solicitation
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
									<div className="p-4 border rounded-lg">
										<div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
											<Calendar className="h-4 w-4" />
											Created
										</div>
										<p className="font-medium">
											{new Date(solicitation.created_at).toLocaleDateString(undefined, {
												year: "numeric",
												month: "long",
												day: "numeric",
											})}
										</p>
									</div>
									{solicitation.publish_date && (
										<div className="p-4 border rounded-lg">
											<div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
												<Send className="h-4 w-4" />
												{new Date(solicitation.publish_date) > new Date() ? "Scheduled" : "Published"}
											</div>
											<p className="font-medium">
												{new Date(solicitation.publish_date).toLocaleDateString(undefined, {
													year: "numeric",
													month: "long",
													day: "numeric",
												})}
											</p>
										</div>
									)}
									{solicitation.due_date && (
										<div className="p-4 border rounded-lg">
											<div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
												<Clock className="h-4 w-4" />
												Due Date
											</div>
											<p className="font-medium">
												{new Date(solicitation.due_date).toLocaleDateString(undefined, {
													year: "numeric",
													month: "long",
													day: "numeric",
												})}
											</p>
										</div>
									)}
								</div>
							</CardContent>
						</Card>
					</TabsContent>
				</Tabs>

				{/* Publish/Schedule Confirmation Dialog */}
				<Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								{solicitation.publish_date && new Date(solicitation.publish_date) > new Date() ? (
									<>
										<Clock className="h-5 w-5" />
										Schedule Solicitation
									</>
								) : (
									<>
										<Send className="h-5 w-5" />
										Publish Solicitation
									</>
								)}
							</DialogTitle>
							<DialogDescription>
								{solicitation.publish_date && new Date(solicitation.publish_date) > new Date() ? (
									<>
										This solicitation will be scheduled to publish on{" "}
										<strong>{new Date(solicitation.publish_date).toLocaleDateString()}</strong>.
										Vendors will be able to view and submit bids once it goes live.
									</>
								) : (
									"This solicitation will be published immediately and visible to vendors. They will be able to submit bids until the due date."
								)}
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={() => setPublishDialogOpen(false)}>
								Cancel
							</Button>
							<Button
								onClick={async () => {
									const isScheduled = solicitation.publish_date && new Date(solicitation.publish_date) > new Date();
									if (isScheduled) {
										await handleTransition("open", "Solicitation scheduled!", solicitation.publish_date!);
									} else {
										await handleTransition("open", "Solicitation published!");
									}
									setPublishDialogOpen(false);
								}}
								disabled={isTransitioning}
							>
								{isTransitioning ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : solicitation.publish_date && new Date(solicitation.publish_date) > new Date() ? (
									<Clock className="mr-2 h-4 w-4" />
								) : (
									<Send className="mr-2 h-4 w-4" />
								)}
								{solicitation.publish_date && new Date(solicitation.publish_date) > new Date() ? "Schedule" : "Publish"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Archive Confirmation Dialog */}
				<Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<Archive className="h-5 w-5" />
								Archive Solicitation
							</DialogTitle>
							<DialogDescription>
								Are you sure you want to archive this solicitation? Archived solicitations
								cannot be edited or viewed by vendors. This action cannot be undone.
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={() => setArchiveDialogOpen(false)}>
								Cancel
							</Button>
							<Button
								onClick={async () => {
									await handleTransition("archived", "Solicitation archived");
									setArchiveDialogOpen(false);
								}}
								disabled={isTransitioning}
							>
								{isTransitioning ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<Archive className="mr-2 h-4 w-4" />
								)}
								Archive
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Back to Draft Confirmation Dialog */}
				<Dialog open={backToDraftDialogOpen} onOpenChange={setBackToDraftDialogOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<ArrowLeft className="h-5 w-5" />
								Move Back to Draft
							</DialogTitle>
							<DialogDescription>
								Are you sure you want to move this solicitation back to draft status?
								The scheduled publish date will be cleared and the solicitation will
								not be visible to vendors.
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={() => setBackToDraftDialogOpen(false)}>
								Cancel
							</Button>
							<Button
								onClick={async () => {
									await handleTransition("draft", "Solicitation moved back to draft");
									setBackToDraftDialogOpen(false);
								}}
								disabled={isTransitioning}
							>
								{isTransitioning ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<ArrowLeft className="mr-2 h-4 w-4" />
								)}
								Back to Draft
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Close Bidding Confirmation Dialog */}
				<Dialog open={closeBiddingDialogOpen} onOpenChange={setCloseBiddingDialogOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<Lock className="h-5 w-5" />
								Close Bidding
							</DialogTitle>
							<DialogDescription>
								Are you sure you want to close bidding on this solicitation?
								Vendors will no longer be able to submit or modify bids.
								The solicitation will move to "Awaiting Decision" status for evaluation.
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={() => setCloseBiddingDialogOpen(false)}>
								Cancel
							</Button>
							<Button
								onClick={async () => {
									await handleCloseBidding();
									setCloseBiddingDialogOpen(false);
								}}
								disabled={isTransitioning}
							>
								{isTransitioning ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<Lock className="mr-2 h-4 w-4" />
								)}
								Close Bidding
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Cancel Solicitation Confirmation Dialog */}
				<Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2 text-destructive">
								<Ban className="h-5 w-5" />
								Cancel Solicitation
							</DialogTitle>
							<DialogDescription>
								Are you sure you want to cancel this solicitation? This action cannot be undone.
								All vendors who have submitted bids will be notified of the cancellation.
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
								Go Back
							</Button>
							<Button
								variant="destructive"
								onClick={async () => {
									await handleTransition("cancelled", "Solicitation cancelled");
									setCancelDialogOpen(false);
								}}
								disabled={isTransitioning}
							>
								{isTransitioning ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<Ban className="mr-2 h-4 w-4" />
								)}
								Cancel Solicitation
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Award Dialog */}
				<Dialog open={awardDialogOpen} onOpenChange={setAwardDialogOpen}>
					<DialogContent className="max-w-2xl">
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<Trophy className="h-5 w-5" />
								Award Contract
							</DialogTitle>
							<DialogDescription>
								Select vendor(s) to award this contract and configure award
								details.
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
							{/* Vendor Selection */}
							<div className="space-y-3">
								<Label>Select Vendor(s) to Award</Label>
								<div className="space-y-2 border rounded-lg p-3">
									{rankedBids.map((bid) => (
										<div
											key={bid.id}
											className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
												selectedVendors.has(bid.id)
													? "bg-primary/10 border-primary"
													: "hover:bg-muted/50"
											}`}
											onClick={() => handleToggleVendorSelection(bid.id)}
										>
											<div className="flex items-center gap-3">
												<Checkbox
													checked={selectedVendors.has(bid.id)}
													onCheckedChange={() =>
														handleToggleVendorSelection(bid.id)
													}
												/>
												<div>
													<div className="flex items-center gap-2">
														{bid.rank === 1 && (
															<Trophy className="h-4 w-4 text-yellow-500" />
														)}
														<span className="font-medium">
															{bid.vendorName}
														</span>
														<Badge variant="outline">#{bid.rank}</Badge>
													</div>
													<p className="text-sm text-muted-foreground">
														Score: {bid.hasMultipleRowScores ? "Multiple" : bid.score !== null ? `${bid.score}%` : "-"}
													</p>
												</div>
											</div>
										</div>
									))}
									{rankedBids.length === 0 && (
										<p className="text-center py-4 text-muted-foreground">
											No bids available to award
										</p>
									)}
								</div>
							</div>

							{/* Contract Dates */}
							<div className="grid gap-4 md:grid-cols-2">
								<div className="space-y-2">
									<Label htmlFor="contractStartDate">
										Contract Start Date *
									</Label>
									<Input
										id="contractStartDate"
										type="date"
										value={awardForm.contractStartDate}
										onChange={(e) =>
											setAwardForm((prev) => ({
												...prev,
												contractStartDate: e.target.value,
											}))
										}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="contractEndDate">Contract End Date *</Label>
									<Input
										id="contractEndDate"
										type="date"
										value={awardForm.contractEndDate}
										onChange={(e) =>
											setAwardForm((prev) => ({
												...prev,
												contractEndDate: e.target.value,
											}))
										}
									/>
								</div>
							</div>

							{/* Award Attachments */}
							<div className="space-y-3">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<Upload className="h-4 w-4 text-muted-foreground" />
										<Label>Award Documents (Optional)</Label>
									</div>
									<div>
										<input
											ref={awardAttachmentInputRef}
											type="file"
											accept=".pdf,.doc,.docx"
											className="hidden"
											onChange={handleAwardAttachmentUpload}
										/>
										<Button
											variant="outline"
											size="sm"
											onClick={() => awardAttachmentInputRef.current?.click()}
											disabled={isUploadingAwardAttachment}
										>
											{isUploadingAwardAttachment ? (
												<Loader2 className="h-4 w-4 mr-1 animate-spin" />
											) : (
												<Upload className="h-4 w-4 mr-1" />
											)}
											{isUploadingAwardAttachment ? "Uploading..." : "Upload"}
										</Button>
									</div>
								</div>
								<p className="text-sm text-muted-foreground">
									Upload contracts, terms, and other documents vendors can download.
								</p>
								{pendingAwardAttachments.length > 0 ? (
									<div className="space-y-2 border rounded-lg p-3 max-h-[150px] overflow-y-auto">
										{pendingAwardAttachments.map((attachment, index) => (
											<div
												key={attachment.storagePath}
												className="flex items-center justify-between p-2 rounded-lg border hover:bg-muted/50"
											>
												<div className="flex items-center gap-3 min-w-0 flex-1">
													<FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
													<div className="min-w-0">
														<p className="font-medium text-sm truncate">
															{attachment.name}
														</p>
														<p className="text-xs text-muted-foreground">
															{(attachment.size / 1024).toFixed(1)} KB
														</p>
													</div>
												</div>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => handleRemoveAwardAttachment(index)}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-4 border rounded-lg bg-muted/30">
										<Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground opacity-50" />
										<p className="text-sm text-muted-foreground">
											No documents uploaded yet
										</p>
									</div>
								)}
							</div>

							{/* Required Forms */}
							<div className="space-y-3">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<FileText className="h-4 w-4 text-muted-foreground" />
										<Label>Required Forms (Optional)</Label>
									</div>
									<Button
										variant="outline"
										size="sm"
										onClick={handleAddAwardForm}
									>
										<Plus className="h-4 w-4 mr-1" />
										Add Form
									</Button>
								</div>
								<p className="text-sm text-muted-foreground">
									Create forms that vendors must complete to finalize the contract.
								</p>
								{pendingAwardForms.length > 0 ? (
									<div className="space-y-2 border rounded-lg p-3 max-h-[200px] overflow-y-auto">
										{pendingAwardForms.map((form, index) => (
											<div
												key={form.id}
												className="flex items-center justify-between p-2 rounded-lg border hover:bg-muted/50"
											>
												<div className="flex items-center gap-3 min-w-0 flex-1">
													<FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
													<div className="min-w-0">
														<p className="font-medium text-sm truncate">
															{form.name}
														</p>
														{form.description && (
															<p className="text-xs text-muted-foreground truncate">
																{form.description}
															</p>
														)}
													</div>
													<Badge variant="secondary" className="flex-shrink-0">Required</Badge>
												</div>
												<div className="flex items-center gap-1 flex-shrink-0">
													<Button
														variant="ghost"
														size="sm"
														onClick={() => handleEditAwardForm(index)}
													>
														<Edit className="h-4 w-4" />
													</Button>
													<Button
														variant="ghost"
														size="sm"
														onClick={() => handleRemoveAwardForm(index)}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-6 border rounded-lg bg-muted/30">
										<FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
										<p className="text-sm text-muted-foreground">
											No forms added yet
										</p>
										<p className="text-xs text-muted-foreground">
											Click "Add Form" to create forms for this award
										</p>
									</div>
								)}
								{pendingAwardForms.length > 0 && (
									<p className="text-sm text-muted-foreground">
										{pendingAwardForms.length} form{pendingAwardForms.length !== 1 ? "s" : ""} will be created
									</p>
								)}
							</div>

							{/* Email Notification */}
							<div className="flex items-center space-x-2">
								<Checkbox
									id="sendEmail"
									checked={awardForm.sendEmail}
									onCheckedChange={(checked) =>
										setAwardForm((prev) => ({
											...prev,
											sendEmail: checked as boolean,
										}))
									}
								/>
								<Label htmlFor="sendEmail" className="font-normal">
									Send award notification email to selected vendor(s)
								</Label>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setAwardDialogOpen(false)}
								disabled={isAwarding}
							>
								Cancel
							</Button>
							<Button
								onClick={handleSubmitAward}
								disabled={selectedVendors.size === 0 || isAwarding}
							>
								{isAwarding ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<Trophy className="mr-2 h-4 w-4" />
								)}
								{isAwarding
									? "Awarding..."
									: `Award Contract (${selectedVendors.size} vendor${selectedVendors.size !== 1 ? "s" : ""})`}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Award Form Editor */}
				<PdfFormEditorV2
					open={awardFormEditorOpen}
					onClose={() => {
						setAwardFormEditorOpen(false);
						setEditingAwardFormIndex(null);
						// Reopen the award dialog
						setAwardDialogOpen(true);
					}}
					onSave={handleSaveAwardForm}
					initialPdfStoragePath={
						editingAwardFormIndex !== null
							? pendingAwardForms[editingAwardFormIndex]?.pdfStoragePath ?? null
							: null
					}
					formName={
						editingAwardFormIndex !== null
							? pendingAwardForms[editingAwardFormIndex]?.name ?? ""
							: ""
					}
					formDescription={
						editingAwardFormIndex !== null
							? pendingAwardForms[editingAwardFormIndex]?.description ?? ""
							: ""
					}
					organizationId={user?.organizationId ?? ""}
				/>
			</div>
		</DashboardLayout>
	);
}
